import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { ProviderPatient } from '@models';

/**
 * Exposes the patient whose chart is currently open, derived from the resolved
 * `patient` route data set by `providerPatientResolver` at `patient-list/:id`.
 *
 * Lets shell-level components (e.g. the Ask AI header input) know the current
 * patient without being inside the patient route's component tree. Emits `null`
 * on any screen without a patient in context.
 */
@Injectable({ providedIn: 'root' })
export class CurrentPatientService {
  private readonly router = inject(Router);

  readonly currentPatient = signal<ProviderPatient | null>(
    this.resolveCurrentPatient()
  );

  constructor() {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => this.currentPatient.set(this.resolveCurrentPatient()));
  }

  private resolveCurrentPatient(): ProviderPatient | null {
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;

    while (route) {
      const patient = route.data['patient'] as ProviderPatient | undefined;
      if (patient) return patient;
      route = route.firstChild;
    }

    return null;
  }
}
